import { Link, usePage } from '@inertiajs/react'
import { t } from '@/lib/i18n'

// 顺序照 docs/designs/mobile：看板 / 目标 / 打卡(FAB) / 商店 / 我的。
// 打卡在正中间且上浮成 FAB —— 它是这个产品每天唯一必做的动作，其余四格是去看、去改。
//
// 🎯 这一格指向 /goals（列表）而不是某个目标的详情：原型里指详情只是因为它只有
// 一条假数据，一个 tab 不能指向某一条记录。
//
// 标签写成**惰性**的 t() 字面量调用，两个约束同时满足：
//   * 不能在模块级就求值——模块顶层在 import 阶段执行，那时 initCatalog() 还没跑，
//     目录是空的，标签会全变成 key 本身（dev 下直接抛异常）。所以包一层函数。
//   * 必须是字面量 t('…') 而不是先存 key 再 t(key)——test/i18n_client_keys_test.rb
//     靠静态扫描 TSX 判断哪些 key 有人用，间接引用会被误判成死 key。
const TABS = [
  { href: '/dashboard', icon: '📊', fab: false, label: () => t('layout.tabs.dashboard') },
  { href: '/goals', icon: '🎯', fab: false, label: () => t('layout.tabs.goals') },
  { href: '/checkin', icon: '✓', fab: true, label: () => t('layout.tabs.checkin') },
  { href: '/store', icon: '🛍️', fab: false, label: () => t('layout.tabs.store') },
]

export default function TabBar({ onMore }: { onMore: () => void }) {
  const url = usePage().url
  // startsWith 而不是相等：/goals 这一格在 /goals/12 上也该亮着。
  const isActive = (href: string) => url.startsWith(href)

  return (
    <nav
      aria-label={t('layout.tabs.nav')}
      className="fixed left-0 right-0 bottom-0 z-40 grid grid-cols-5 bg-card border-t border-line h-tabbar-safe pb-safe"
    >
      {TABS.map((tab) => {
        const on = isActive(tab.href)
        return (
          <Link
            key={tab.href}
            href={tab.href}
            aria-current={on ? 'page' : undefined}
            className={
              'flex flex-col items-center justify-center gap-0.5 min-h-tabbar text-[11px] font-semibold ' +
              (on ? 'text-brand' : 'text-faint')
            }
          >
            {tab.fab ? (
              // FAB 上浮 20px 压在 tab bar 上沿。-mt-5 = -20px。
              <span
                aria-hidden="true"
                className="w-[46px] h-[46px] -mt-5 rounded-full grid place-items-center bg-gradient-to-br from-brand to-brand2 text-white text-[21px] font-extrabold shadow-fab"
              >
                {tab.icon}
              </span>
            ) : (
              <i aria-hidden="true" className="not-italic text-xl leading-none">{tab.icon}</i>
            )}
            <span>{tab.label()}</span>
          </Link>
        )
      })}

      {/* 第 5 格不是页面而是一个 sheet：成就 / 流水 / 人员 / 账号 / 订阅 / 音效 / 退出
          这七项各自都够不上一个 tab，但都得有地方去。 */}
      <button
        type="button"
        onClick={onMore}
        aria-haspopup="dialog"
        className="flex flex-col items-center justify-center gap-0.5 min-h-tabbar text-[11px] font-semibold text-faint"
      >
        <i aria-hidden="true" className="not-italic text-xl leading-none">☰</i>
        <span>{t('layout.tabs.mine')}</span>
      </button>
    </nav>
  )
}
